import type { AiDisplayImage, User } from 'src/shared/graphql/generated';

import { CreatedFrom, FINAL_GENERATED_IMAGE_STATUSES } from '../static_values';
import type {
  AiImage,
  AiOrSampleDisplayImage,
  AiOrSampleGeneratedImage,
  AiOrSampleOriginalImage,
  Aspect,
} from '../types';
import { isSampleGenerated } from '../types';

import type { RgbColor } from './color';

export const isGeneratingImage = (image?: AiOrSampleGeneratedImage | null) => {
  if (!image || isSampleGenerated(image)) return false;

  const { generatedImagePath, status, clientStatus } = image;

  return (
    !generatedImagePath &&
    !FINAL_GENERATED_IMAGE_STATUSES.includes(status ?? '') &&
    !FINAL_GENERATED_IMAGE_STATUSES.includes(clientStatus ?? '')
  );
};

export const isOriginalImageDisplay = (image?: AiDisplayImage | null) =>
  image?.createdFrom === CreatedFrom.ORIGINAL && !image.aiGeneratedImage.edits;

/**
 * Determine whether an image is `interior` or `exterior` based on its
 * `spaceType`. Note that `exterior` is also a `spaceType`, but we have many
 * `spaceType`s for interior images.
 */
export const getImageAspect = ({
  spaceType,
}: Pick<AiOrSampleDisplayImage | AiImage, 'spaceType'>): Aspect =>
  spaceType === 'exterior' ? 'exterior' : 'interior';

/**
 * If the user is a homeowner, we check if the image is part of a pro project
 * and get that pro's org id
 *
 * For pros, just return the user org id
 */
export const getImageOrgId = (
  originalImage?: AiOrSampleOriginalImage | null,
  user?: Partial<User> | undefined,
) => {
  const userOrgId = user?.orgs?.[0].id;
  return user?.isHomeowner
    ? originalImage?.associatedProject?.publicOrgInfo?.id
    : userOrgId;
};

export type ImagePixelData = {
  width: number;
  height: number;
  data: Uint8ClampedArray;
};

/**
 * Given an HTML image element or a URL, extract the pixel data once the image
 * has been loaded
 *
 * @param source HTML image element or URL string
 *
 * @returns object containing image dimensions and pixel data
 */
export const getImageData = async (
  source: HTMLImageElement | string,
): Promise<ImagePixelData> => {
  const getDataFromImage = (
    element: HTMLImageElement,
  ): Promise<ImagePixelData> =>
    new Promise((resolve, reject) => {
      const canvas = document.createElement('canvas');
      const context = canvas.getContext('2d');

      if (!context) {
        reject(new Error('Could not get canvas context'));
        return;
      }

      const loadHandler = () => {
        canvas.width = element.naturalWidth;
        canvas.height = element.naturalHeight;
        context.drawImage(element, 0, 0, canvas.width, canvas.height);

        const imageData = context.getImageData(
          0,
          0,
          canvas.width,
          canvas.height,
        );

        resolve({
          width: canvas.width,
          height: canvas.height,
          data: imageData.data,
        });
      };

      if (element.complete) {
        loadHandler();
      } else {
        element.addEventListener('load', loadHandler);
        element.addEventListener('error', () =>
          reject(new Error('Image failed to load')),
        );
      }
    });

  if (typeof source === 'string') {
    const image = new Image();

    // Enable CORS if the image is from a different origin
    image.crossOrigin = 'Anonymous';
    image.src = source;

    return getDataFromImage(image);
  }

  return getDataFromImage(source);
};

export interface AverageColorOptions {
  /**
   * Target point `x` in pixels
   */
  x: number;
  /**
   * Target point `y` in pixels
   */
  y: number;
  /**
   * Sample threshold width and height in pixels
   */
  threshold: number;
}

/**
 * Calculate the average color of an image, optionally taking a target point in pixels to
 * sample up to a specified width and height
 *
 * @example
 * ```ts
 * // Sample a 20px by 20px square centered on the point (100, 100) of the image
 * calculateAverageImageColor(pixelData, { x: 100, y: 100, threshold: 20 });
 * ```
 *
 * @param pixelData Image pixel data
 * @param options Optional sample target point (in pixels) and threshold
 *
 * @returns Average image color
 */
export const calculateAverageImageColor = (
  pixelData: ImagePixelData,
  options?: AverageColorOptions,
): RgbColor | null => {
  const { width, height, data } = pixelData;

  let startX: number;
  let startY: number;
  let endX: number;
  let endY: number;

  if (options) {
    const { x, y, threshold } = options;

    const halfThreshold = Math.floor(threshold / 2);

    startX = Math.max(0, Math.round(x) - halfThreshold);
    startY = Math.max(0, Math.round(y) - halfThreshold);
    endX = Math.min(width, startX + threshold);
    endY = Math.min(height, startY + threshold);
  } else {
    startX = 0;
    startY = 0;
    endX = width;
    endY = height;
  }

  let r = 0;
  let g = 0;
  let b = 0;
  let pixelCount = 0;

  /* eslint-disable no-plusplus */
  for (let y = startY; y < endY; y++) {
    for (let x = startX; x < endX; x++) {
      const i = (y * width + x) * 4;

      r += data[i];
      g += data[i + 1];
      b += data[i + 2];

      pixelCount++;
    }
  }

  if (pixelCount === 0) return null;

  r = Math.round(r / pixelCount);
  g = Math.round(g / pixelCount);
  b = Math.round(b / pixelCount);

  return `rgb(${r}, ${g}, ${b})`;
};
